import { Button, IconButton } from "@mui/material"
import { Font } from "components/shopFeatures/wizard/constants"
import { ElementType } from "react"
import styled, { css, keyframes } from "styled-components"
import { Swiper } from "swiper/react"
import colors from "theme/colors"
import { HomePageContainer } from "theme/shared"

type CtaLinkProps = { component?: ElementType; href?: string }

export const HERO_ACCENT = colors.newPrimaryRed

// Exact Figma gradient overlays (right->left wash behind text + bottom-left wash)
export const HERO_GRADIENT_PRIMARY =
  "linear-gradient(270deg, rgba(15,12,10,0.736) 0%, rgba(15,12,10,0.48) 45%, rgba(15,12,10,0.16) 75%, rgba(15,12,10,0) 100%)"
export const HERO_GRADIENT_CORNER =
  "linear-gradient(36.5deg, rgba(0,0,0,0.56) 0%, rgba(0,0,0,0.28) 18%, rgba(0,0,0,0) 40%)"

export const HeroSection = styled.section`
  position: relative;
  /* Sit above the brand Story's fixed full-viewport white-fade overlay (z-index 1),
     which otherwise covers the hero on load. */
  z-index: 2;
  width: 100%;
  min-height: 820px;
  overflow: hidden;
  color: ${(p) => p.theme.colors.white};
`

export const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start; /* flex-start = right in RTL */
  gap: 24px;
  text-align: right;
  max-width: 720px;
`

/* Reserve the badge row height even on slides without a badge, so the title
   below it keeps a fixed position across slides (DLK-808). */
export const HeroBadgeRow = styled.div<{ $hasBothText: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start; /* flex-start = right in RTL */
  justify-content: ${(p) => (p.$hasBothText ? "space-between" : "flex-end")};
  min-height: calc(26px + 26px + 24px);
  ${(p) => p.theme.breakpoints.down("md")} {
    min-height: calc(18px + 26px + 18px);
  }
`

export const HeroBadge = styled.span`
  display: inline-flex;
  align-items: center;
  background: ${HERO_ACCENT};
  color: ${(p) => p.theme.colors.white};
  font-family: ${Font.MazdaType};
  font-size: 14px;
  line-height: 18px;
  font-weight: 700;
  letter-spacing: 2px;
  padding: 4px 10px;
  min-height: 26px;
`

export const HeroEyebrow = styled.p`
  margin: 0;
  font-family: ${Font.MazdaType};
  font-weight: 700;
  font-size: 18px;
  line-height: 26px;
  letter-spacing: 5.4px;
  ${(p) => p.theme.breakpoints.down("md")} {
    font-size: 14px;
    line-height: 18px;
    font-weight: 400;
    letter-spacing: 0;
  }
`

export const HeroTitle = styled.h1`
  margin: 0;
  font-family: ${Font.MazdaType};
  font-weight: 400;
  font-size: 80px;
  line-height: 88px;
  max-width: 720px;

  ${(p) => p.theme.breakpoints.down("md")} {
    font-size: 44px;
    line-height: 50px;
  }
`

export const HeroDescription = styled.p`
  margin: 0;
  font-family: ${Font.MazdaType};
  font-weight: 300;
  font-size: 20px;
  line-height: 28px;
  max-width: 520px;

  ${(p) => p.theme.breakpoints.down("md")} {
    font-size: 16px;
    line-height: 24px;
    display: -webkit-box;
    -webkit-line-clamp: 3; /* mobile: max 3 lines per char-limit table */
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`

export const PriceBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 92px;
  /* Figma keeps the red accent bar and content on the block's physical LEFT
     edge (not RTL-logical), with the price value reading LTR. */
  border-left: 4px solid ${HERO_ACCENT};
  padding-left: 28px;
  text-align: left;
`
export const PriceLabel = styled.span`
  font-family: ${Font.MazdaType};
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 8px;
`
export const PriceValueRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 4px;
  font-family: ${Font.MazdaType};
  font-weight: 400;
  direction: ltr; /* keep ₪ to the left of the digits */
  justify-content: flex-start;
`
export const PriceCurrency = styled.span`
  font-size: 16px;
  line-height: 16px;
  opacity: 0.85;
`
export const PriceValue = styled.span`
  font-size: 48px;
  line-height: 48px;
`
export const PriceNote = styled.span`
  font-family: ${Font.MazdaType};
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
`

export const CtaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  ${(p) => p.theme.breakpoints.down("md")} {
    flex-direction: column;
    align-items: stretch;
  }
`
// Neutralise MUI Button defaults + theme MuiButton variant overrides (via &&)
// and apply the exact Figma CTA spec.
const ctaBase = css`
  text-transform: none;
  border-radius: 0;
  box-shadow: none;
  min-width: 0;
  padding: 18px 26px;
  border: 2px solid;
  font-family: ${Font.MazdaType};
  font-size: 16px;
  line-height: 18px;
  letter-spacing: 0; /* override MUI button typography letter-spacing */
  white-space: nowrap;
  text-decoration: none;
  &:hover {
    box-shadow: none;
  }
  ${(p) => p.theme.breakpoints.down("md")} {
    width: 100%;
  }
`
export const CtaPrimary = styled(Button)<CtaLinkProps>`
  && {
    ${ctaBase}
    background-color: ${HERO_ACCENT};
    border-color: ${HERO_ACCENT};
    color: ${(p) => p.theme.colors.white};
    font-weight: 300;
    &:hover {
      background-color: ${HERO_ACCENT};
      border-color: ${HERO_ACCENT};
    }
  }
`
export const CtaSecondary = styled(Button)<CtaLinkProps>`
  && {
    ${ctaBase}
    background-color: transparent;
    border-color: ${(p) => p.theme.colors.white};
    color: ${(p) => p.theme.colors.white};
    font-weight: 400;
    &:hover {
      background-color: rgba(255, 255, 255, 0.12);
      border-color: ${(p) => p.theme.colors.white};
    }
  }
`

export const HeroBg = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;
`
export const HeroBgImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`
export const HeroBgVideoDesktop = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  ${(p) => p.theme.breakpoints.down("md")} {
    display: none;
  }
`
export const HeroBgVideoMobile = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  ${(p) => p.theme.breakpoints.up("md")} {
    display: none;
  }
`
export const HeroBgImageDesktop = styled(HeroBgImage)`
  ${(p) => p.theme.breakpoints.down("md")} {
    display: none;
  }
`
export const HeroBgImageMobile = styled(HeroBgImage)`
  ${(p) => p.theme.breakpoints.up("md")} {
    display: none;
  }
`
export const HeroBgOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background: ${HERO_GRADIENT_PRIMARY}, ${HERO_GRADIENT_CORNER};
`

export const HeroSwiper = styled(Swiper)`
  width: 100%;
  height: 100%;
  position: absolute;
  inset: 0;

  /* Cross-fade is owned by Swiper's EffectFade module (duration = speed prop).
     Do not set .swiper-slide opacity here — it conflicts with EffectFade. */
`
export const HeroSlideInner = styled.div`
  position: relative;
  width: 100%;
  min-height: 820px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 64px 0 156px;

  ${(p) => p.theme.breakpoints.down("lg")} {
    padding: 64px 0 147px;
  }
  ${(p) => p.theme.breakpoints.down("md")} {
    padding: 24px 0 82px;
  }
`
export const HeroSlideColumn = styled(HomePageContainer)`
  position: relative;
  z-index: 2;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start; /* right in RTL */
  gap: 120px;
  flex-grow: 1;
  justify-content: space-between;
  ${(p) => p.theme.breakpoints.down("md")} {
    gap: 40px;
    justify-content: space-between;
    flex-grow: 1;
  }
`
export const HeroBottomRow = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  ${(p) => p.theme.breakpoints.down("md")} {
    flex-direction: column-reverse;
    align-items: stretch;
    justify-content: space-between;
  }
`

const progressFill = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`
export const HeroNavBar = styled(HomePageContainer)`
  position: absolute;
  z-index: 3;
  right: 50%;
  transform: translateX(50%);
  bottom: 118px;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start; /* flex-start = right in RTL (matches Figma nav position) */
  gap: 24px;
  height: unset;
  ${(p) => p.theme.breakpoints.down("xl")} {
    bottom: 108.91px;
  }
  ${(p) => p.theme.breakpoints.down("md")} {
    bottom: 24px;
    flex-direction: row-reverse;
    justify-content: space-between;
  }
`
export const Dots = styled.div`
  display: flex;
  gap: 10px;
`
export const Dot = styled.button<{
  $active: boolean
  $paused: boolean
  $duration: number
}>`
  position: relative;
  width: 38px;
  height: 24px;
  padding: 11px 0;
  background: transparent;
  border: 0;
  cursor: pointer;
  display: flex;
  align-items: center;

  &::before {
    content: "";
    display: block;
    width: 100%;
    height: 2px;
    background: rgba(255, 255, 255, 0.25);
    transition: background 0.2s;
  }
  &:hover::before {
    background: rgba(255, 255, 255, 0.55);
  }

  ${(p) =>
    p.$active &&
    css`
      &::after {
        content: "";
        position: absolute;
        inset: 11px 0;
        background: #fff;
        transform-origin: right; /* RTL fill */
        animation: ${progressFill} ${p.$duration}s linear forwards;
        animation-play-state: ${p.$paused ? "paused" : "running"};
      }
    `}
`
export const HeroIndex = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`
export const HeroCounter = styled.span`
  font-family: ${Font.MazdaType};
  font-size: 14px;
  font-weight: 400; /* Mazda Type Regular per Figma (was inheriting 300) */
  line-height: 24px;
  letter-spacing: 2.4px;
  direction: ltr; /* keep the NN / NN counter in reading order */
`
export const PlayPauseButton = styled(IconButton).attrs({
  disableRipple: true,
})<{ $paused: boolean }>`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    border: 2px solid ${(p) => p.theme.colors.neutral};
    background: ${(p) => p.theme.colors.neutral};
    color: ${(p) => p.theme.colors.white};
    cursor: pointer;
    padding: 0;
    transition: background 0.2s, transform 0.2s, border-color 0.2s, color 0.2s;

    &:hover {
      background: ${(p) => p.theme.colors.white};
      color: ${(p) =>
        p.$paused ? p.theme.colors.neutral : p.theme.colors.black};
      border-color: ${(p) => p.theme.colors.white};
      transform: scale(1.08);
      svg path {
        fill: black;
      }
    }
    svg {
      width: 11px;
      height: 11px;
      display: block;
      path {
        transition: fill 0.2s;
      }
    }
  }
`
